import React, { useState, useRef, useEffect } from "react";
import { useModalKeyboardHandlers } from "../../useModalKeyboardHandlers";
import { useNotification } from '../../framework/NotificationContext';
import ApiClient from "../../../services/apiClient";
import config from "../../../config";

const imageTypes = ["SEM", "FESEM", "Cross-sectional SEM", "Microscopy", "Simple Image"];

const extraFieldsByType = {
    SEM: [
        { name: "Scale", label: "Scale", type: "number", placeholder: "e.g. 500", unit: "nm" },
        { name: "Magnification", label: "Magnification", type: "number", placeholder: "e.g. 1000", unit: "x" },
    ],
    FESEM: [
        { name: "Scale", label: "Scale", type: "number", placeholder: "e.g. 500", unit: "nm" },
        { name: "Magnification", label: "Magnification", type: "number", placeholder: "e.g. 1000", unit: "x" },
    ],
    "Cross-sectional SEM": [
        { name: "Scale", label: "Scale", type: "number", placeholder: "e.g. 500", unit: "nm" },
        { name: "Magnification", label: "Magnification", type: "number", placeholder: "e.g. 1000", unit: "x" },
    ],
    Microscopy: [
        { name: "magnification", label: "Magnification", type: "text", placeholder: "e.g. 40x" },
        { name: "staining", label: "Staining Method", type: "text", placeholder: "e.g. H&E" },
    ],
    "Simple Image": [
        { name: "description", label: "Description", type: "text", placeholder: "Optional description" },
    ],
};

function base64ToBlob(base64, contentType = "image/png") {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type: contentType });
}

export default function UploadImageModal({ initialImages = [], onClose, onSave }) {
    const fileInputRefs = useRef({});
    const { error } = useNotification();
    const [uploads, setUploads] = useState([]);

    // Sync with props
    useEffect(() => {
        const initData = () => {
            const normalizedInitial = imageTypes.flatMap((type) =>
                (initialImages[type] || []).map((item) => {
                    let file = null;
                    let extraFields = {};
                    let imageId = null;
                    let downloadUrl = null;
                    let status = "success";
                    if (typeof item === "string") {
                        file = base64ToBlob(item);
                        status = "idle"; // need to upload base64 locally if not already uploaded
                    } else if (item) {
                        imageId = item.id;
                        downloadUrl = item.downloadUrl;
                        extraFields = item.imageDefinition || {};
                    }
                    return {
                        id: crypto.randomUUID(),
                        type,
                        file,
                        extraFields,
                        status,
                        progress: imageId ? 100 : 0,
                        imageId,
                        downloadUrl,
                        errorMsg: null
                    };
                })
            );
            return normalizedInitial.length ? normalizedInitial : [{ id: crypto.randomUUID(), type: "", file: null, extraFields: {}, status: "idle", progress: 0, imageId: null, downloadUrl: null, errorMsg: null }];
        };
        setUploads(initData());
    }, [initialImages]);

    // Manage previews
    const [previews, setPreviews] = useState({});

    useEffect(() => {
        // Revoke old previews
        Object.values(previews).forEach((val) => {
            if (val && val.startsWith("blob:")) {
                URL.revokeObjectURL(val);
            }
        });

        const newPreviews = {};
        uploads.forEach(({ id, file, downloadUrl }) => {
            if (file) {
                try {
                    newPreviews[id] = URL.createObjectURL(file);
                } catch (e) {
                    console.error("Failed to create object URL", e);
                }
            } else if (downloadUrl) {
                newPreviews[id] = downloadUrl;
            }
        });
        setPreviews(newPreviews);

        return () => {
            Object.values(newPreviews).forEach((val) => {
                if (val && val.startsWith("blob:")) {
                    URL.revokeObjectURL(val);
                }
            });
        };
    }, [uploads]);

    const uploadSingleImage = (id, file, type, extraFields) => {
        if (!file) return;

        setUploads((prev) =>
            prev.map((u) =>
                u.id === id ? { ...u, status: "uploading", progress: 0, errorMsg: null } : u
            )
        );

        const xhr = new XMLHttpRequest();
        const url = `${config.apiUrl}/images/upload`;

        xhr.open("POST", url, true);

        const token = localStorage.getItem("accessToken");
        if (token) {
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        }

        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                const percent = Math.round((event.loaded / event.total) * 100);
                setUploads((prev) =>
                    prev.map((u) => (u.id === id ? { ...u, progress: percent } : u))
                );
            }
        };

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    const response = JSON.parse(xhr.responseText);
                    setUploads((prev) =>
                        prev.map((u) =>
                            u.id === id
                                ? {
                                    ...u,
                                    status: "success",
                                    progress: 100,
                                    imageId: response.id,
                                    downloadUrl: response.downloadUrl,
                                }
                                : u
                        )
                    );
                } catch (e) {
                    setUploads((prev) =>
                        prev.map((u) =>
                            u.id === id
                                ? { ...u, status: "error", errorMsg: "Failed to parse response" }
                                : u
                        )
                    );
                }
            } else {
                setUploads((prev) =>
                    prev.map((u) =>
                        u.id === id
                            ? {
                                ...u,
                                status: "error",
                                errorMsg: xhr.statusText || `Upload failed (${xhr.status})`,
                            }
                            : u
                    )
                );
            }
        };

        xhr.onerror = () => {
            setUploads((prev) =>
                prev.map((u) => (u.id === id ? { ...u, status: "error", errorMsg: "Network error" } : u))
            );
        };

        const formData = new FormData();
        formData.append("file", file);
        if (type) {
            formData.append("imageType", type);
        }
        if (extraFields && Object.keys(extraFields).length > 0) {
            formData.append("imageDefinition", JSON.stringify(extraFields));
        }

        xhr.send(formData);
    };

    const handleDeleteImage = async (imageId) => {
        try {
            const url = `/images/${imageId}`;
            const response = await ApiClient.delete(url);
            if (!response.ok) {
                console.error("Failed to delete image from server:", response.statusText);
            }
        } catch (err) {
            console.error("Error deleting image from server:", err);
        }
    };

    const handleRetry = (id) => {
        const upload = uploads.find((u) => u.id === id);
        if (upload && upload.file) {
            uploadSingleImage(id, upload.file, upload.type, upload.extraFields);
        }
    };

    const handleTypeChange = (id, newType) => {
        setUploads((prev) =>
            prev.map((upload) =>
                upload.id === id
                    ? {
                        ...upload,
                        type: newType,
                        extraFields:
                            extraFieldsByType[newType]?.reduce((acc, field) => {
                                acc[field.name] = upload.extraFields?.[field.name] || "";
                                return acc;
                            }, {}) || {},
                    }
                    : upload
            )
        );
    };

    const handleFileChange = async (id, e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const upload = uploads.find((u) => u.id === id);
        if (upload && upload.imageId) {
            handleDeleteImage(upload.imageId);
        }

        setUploads((prev) =>
            prev.map((u) =>
                u.id === id
                    ? {
                        ...u,
                        file,
                        status: "idle",
                        progress: 0,
                        imageId: null,
                        downloadUrl: null,
                        errorMsg: null,
                    }
                    : u
            )
        );
        e.target.value = null; // Reset input

        // Trigger immediate upload
        uploadSingleImage(id, file, upload.type, upload.extraFields);
    };

    const handleExtraFieldChange = (id, fieldName, value) => {
        setUploads((prev) =>
            prev.map((upload) =>
                upload.id === id
                    ? { ...upload, extraFields: { ...upload.extraFields, [fieldName]: value } }
                    : upload
            )
        );
    };

    const handleRemoveUpload = async (id) => {
        const upload = uploads.find((u) => u.id === id);
        if (upload && upload.imageId) {
            await handleDeleteImage(upload.imageId);
        }
        setUploads((prev) => prev.filter((upload) => upload.id !== id));
    };

    const handleAddNew = () =>
        setUploads((prev) => [
            ...prev,
            {
                id: crypto.randomUUID(),
                type: "",
                file: null,
                extraFields: {},
                status: "idle",
                progress: 0,
                imageId: null,
                downloadUrl: null,
                errorMsg: null,
            },
        ]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (uploads.some(({ type, file, imageId }) => !type || (!file && !imageId))) {
            error("Please select type and upload an image for all entries.");
            return;
        }

        if (uploads.some(({ status }) => status === "uploading")) {
            error("Please wait for all image uploads to complete.");
            return;
        }

        if (uploads.some(({ status }) => status === "error")) {
            error("Please resolve or remove failed uploads.");
            return;
        }

        try {
            const fiberImages = uploads.map(({ type, extraFields, imageId, downloadUrl }) => ({
                id: imageId,
                imageType: type,
                imageDefinition: extraFields,
                downloadUrl: downloadUrl,
            }));

            onSave({ fiberImages });
            onClose();
        } catch (err) {
            error("Error saving images: " + err.message);
            console.error(err);
        }
    };

    useModalKeyboardHandlers({
        onClose,
        onSave: () => handleSubmit({ preventDefault: () => { } }),
        dependencies: [uploads],
    });

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} role="dialog">
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <form className="modal-content shadow-lg border-0" onSubmit={handleSubmit}>
                    <div className="modal-header bg-light">
                        <h5 className="modal-title fw-bold text-dark">
                            <i className="bi bi-images me-2 text-primary"></i>
                            Upload Image Evidence
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                    </div>

                    <div className="modal-body p-4 bg-light-subtle">
                        {uploads.map(({ id, type, file, extraFields, status, progress, downloadUrl, errorMsg }, idx) => (
                            <div key={id} className="card border-light shadow-sm mb-4">
                                <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
                                    <h6 className="fw-bold text-secondary mb-0">Image #{idx + 1}</h6>
                                    {uploads.length > 1 && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger border-0"
                                            onClick={() => handleRemoveUpload(id)}
                                            title="Remove Image"
                                        >
                                            <i className="bi bi-trash-fill"></i>
                                        </button>
                                    )}
                                </div>
                                <div className="card-body p-3">
                                    <div className="mb-3">
                                        <label className="form-label fw-bold text-secondary">
                                            Image Type <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className="form-select shadow-sm"
                                            value={type}
                                            onChange={(e) => handleTypeChange(id, e.target.value)}
                                            required
                                        >
                                            <option value="" disabled>-- Select type --</option>
                                            {imageTypes.map((t) => (
                                                <option key={t} value={t} disabled={uploads.some((u) => u.type === t && u.id !== id)}>
                                                    {t}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {type && extraFieldsByType[type] && (
                                        <div className="row g-3 mb-3">
                                            {extraFieldsByType[type].map(({ name, label, type: inputType, placeholder, unit }) => (
                                                <div key={name} className="col-md-6">
                                                    <label className="form-label fw-bold text-secondary">{label}</label>
                                                    <div className="input-group">
                                                        <input
                                                            type={inputType}
                                                            className="form-control shadow-sm"
                                                            value={extraFields?.[name] || ""}
                                                            placeholder={placeholder}
                                                            onChange={(e) => handleExtraFieldChange(id, name, e.target.value)}
                                                        />
                                                        {unit && <span className="input-group-text bg-white text-muted">{unit}</span>}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="mb-2">
                                        <label className="form-label fw-bold text-secondary d-block">File Upload</label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleFileChange(id, e)}
                                            ref={(el) => (fileInputRefs.current[id] = el)}
                                            className="d-none"
                                        />

                                        {!file && !downloadUrl && (
                                            <div
                                                className="border border-2 border-dashed rounded p-4 text-center cursor-pointer hover-bg-light"
                                                style={{ borderStyle: 'dashed', borderColor: '#cbd5e1' }}
                                                onClick={() => fileInputRefs.current[id]?.click()}
                                            >
                                                <i className="bi bi-cloud-arrow-up display-6 text-muted mb-2"></i>
                                                <p className="text-secondary mb-0">Click to browse or drag image here</p>
                                            </div>
                                        )}

                                        {(file || downloadUrl) && previews[id] && (
                                            <div className="position-relative mt-2 text-center bg-light rounded p-3 border">
                                                <img
                                                    src={previews[id]}
                                                    alt="Preview"
                                                    className="img-fluid rounded shadow-sm"
                                                    style={{ maxHeight: '200px' }}
                                                />
                                                <div className="mt-2">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-secondary me-2"
                                                        onClick={() => fileInputRefs.current[id]?.click()}
                                                    >
                                                        Change
                                                    </button>
                                                    <small className="text-muted">
                                                        {file ? `${file.name} (${Math.round(file.size / 1024)} KB)` : "Already Uploaded"}
                                                    </small>
                                                </div>
                                            </div>
                                        )}

                                        {status === "uploading" && (
                                            <div className="mt-3">
                                                <div className="progress" style={{ height: '8px' }}>
                                                    <div
                                                        className="progress-bar progress-bar-striped progress-bar-animated"
                                                        role="progressbar"
                                                        style={{ width: `${progress}%` }}
                                                    ></div>
                                                </div>
                                                <div className="d-flex justify-content-between mt-1">
                                                    <small className="text-muted">Uploading...</small>
                                                    <small className="text-muted">{progress}%</small>
                                                </div>
                                            </div>
                                        )}

                                        {status === "success" && file && (
                                            <div className="mt-2 text-success small">
                                                <i className="bi bi-check-circle-fill me-1"></i> Uploaded successfully!
                                            </div>
                                        )}

                                        {status === "error" && (
                                            <div className="mt-2 p-2 bg-danger-subtle rounded border border-danger-subtle d-flex justify-content-between align-items-center">
                                                <small className="text-danger fw-bold">
                                                    <i className="bi bi-exclamation-triangle-fill me-1"></i> {errorMsg || "Upload failed"}
                                                </small>
                                                <button
                                                    type="button"
                                                    className="btn btn-xs btn-outline-danger py-0 px-2 small"
                                                    onClick={() => handleRetry(id)}
                                                >
                                                    Retry
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}

                        <button
                            type="button"
                            className="btn btn-outline-primary w-100 py-2 border-dashed"
                            style={{ borderStyle: 'dashed' }}
                            onClick={handleAddNew}
                            disabled={uploads.length >= imageTypes.length}
                        >
                            <i className="bi bi-plus-lg me-2"></i> Add Another Image
                        </button>
                    </div>

                    <div className="modal-footer bg-light border-top-0 py-3">
                        <button type="button" className="btn btn-outline-secondary px-4 rounded-pill fw-bold me-2" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary px-4 shadow-sm fw-bold rounded-pill">
                            <i className="bi bi-check2-circle me-1"></i>
                            Save Images
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
